import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type TableCellProps,
} from "@mui/material";
import {
  DetailDataCard,
  DetailDataCardField,
  detailDataCardsScrollStackSx,
  detailDataTableOnlySx,
} from "./DetailResponsiveDataCards";

export type ResponsiveRowColumn<T> = {
  /** Stable key for React lists */
  id: string;
  header: React.ReactNode;
  /** Cell content for table and card body */
  render: (row: T) => React.ReactNode;
  align?: TableCellProps["align"];
};

type Props<T> = {
  columns: ResponsiveRowColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  minTableWidth?: number;
  emptyContent?: React.ReactNode;
};

/**
 * MUI `Table` from `md` up; stacked outlined cards below `md` (scrollable list).
 * Use for detail sections that are not backed by `DataTable` / Data Grid.
 */
export function ResponsiveRowTableCards<T>({
  columns,
  rows,
  getRowKey,
  minTableWidth = 640,
  emptyContent = null,
}: Props<T>) {
  if (rows.length === 0) {
    return <>{emptyContent}</>;
  }

  return (
    <>
      <TableContainer
        sx={{ width: "100%", overflowX: "auto", ...detailDataTableOnlySx }}
      >
        <Table size="small" sx={{ minWidth: minTableWidth }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 600 }}>
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={getRowKey(row)}>
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align}>
                    {col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={detailDataCardsScrollStackSx}>
        {rows.map((row) => (
          <DetailDataCard key={getRowKey(row)}>
            {columns.map((col) => (
              <DetailDataCardField key={col.id} label={col.header}>
                {col.render(row)}
              </DetailDataCardField>
            ))}
          </DetailDataCard>
        ))}
      </Box>
    </>
  );
}
